/*
 * Voyado Carousel — shared scroll engine for the Voyado-fed product-row carousels.
 * --------------------------------------------------------------------------------
 * One module, one engine. The four consolidated controllers (recommendations,
 * recently-viewed, secondary-list, favorites) and the generic product-row section
 * all attach to this. Each surface keeps only its own fetch + data-extract; the
 * scroll mechanics, the slide-wrapper construction, the cards-rendered dispatch,
 * the controls show/hide mirror and the teardown live here once.
 *
 * window.VoyadoCarousel.attach(section, opts) — mirrors the shipped
 * window.VoyadoPlp.attach(host, { fetch }) precedent (voyado-plp-controller.js):
 * a shared controller parameterised by an opts.fetch fn that returns a NORMALISED
 * array of colour-groups ([[p0,p1,…], …]) — the engine never knows a Voyado
 * response shape, algorithm, productKey or trigger.
 *
 * It owns its scroll engine (NOT product-row.js — type=module, no window export,
 * no re-init path — L-VPR-1). It builds the slide wrapper (L-VPR-2) and dispatches
 * voyado:cards-rendered on document after replaceChildren (L-VPR-3) so the shipped
 * voyado-card-behaviour.js binds gallery/swatches/wishlist on the injected cards.
 *
 * Loaded section-scoped + URL-deduped (type=module) — never added to theme.liquid.
 */
;(function () {
  'use strict';

  if (window.VoyadoCarousel) return;       // module runs once even if URL-deduped twice

  var instances = new WeakMap();            // double-attach guard (one controller per section)

  function createController(section, opts) {
    opts = opts || {};
    var fetch        = opts.fetch;          // required: () => Promise<groups>
    var trigger      = opts.trigger || 'intersection';
    var rerenders    = opts.rerenders === true;

    var track    = section.querySelector('[data-slider-track]');
    var controls = section.querySelector('[data-slider-controls]');
    var prevBtn  = controls && controls.querySelector('[data-slider-prev]');
    var nextBtn  = controls && controls.querySelector('[data-slider-next]');
    var desktopVisible = parseInt(section.getAttribute('data-slider-desktop-visible'), 10) || 4;

    var ac = new AbortController();         // teardown signal
    var fetched = false;                    // single-fetch guard (intersection surfaces)
    var io = null;
    var DESKTOP = parseInt(getComputedStyle(document.documentElement)
                    .getPropertyValue('--breakpoint-lg'), 10) || 1024;

    // ---- trigger: lazy fetch on first viewport intersection (default) ----
    // 'event' surfaces (secondary-list) do NOT observe — the thin file drives
    // render(groups) itself from its own event.
    if (trigger === 'intersection') {
      io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting || fetched) return;
        fetched = true;
        io.disconnect();
        load();
      }, { rootMargin: '200px', threshold: 0 });
      io.observe(section);
    }

    function load() {
      if (typeof fetch !== 'function') { return hide('error'); }
      Promise.resolve()
        .then(function () { return fetch(); })
        .then(function (groups) {
          if (ac.signal.aborted) return;
          groups = groups || [];
          if (groups.length === 0) return hide('empty');
          render(groups);
        })
        .catch(function () {
          if (ac.signal.aborted) return;
          hide('error');
        });
    }

    function hide(state) {
      // empty/error → collapse the carousel; show the matching status node via the
      // host state attribute (mirrors _voyado-grid.css). For a row with nothing to
      // show this is the clean-hide path (display:none — §4.6 generalised collapse).
      section.setAttribute('data-voyado-state', state);
    }

    function render(groups) {
      groups = groups || [];
      if (groups.length === 0) { hide('empty'); track.replaceChildren(); return; }
      var frag = document.createDocumentFragment();
      groups.forEach(function (ps, i) {
        // ps = a colour group; ps[0] is the main product, siblings drive swatches
        // + hover-swap (colour-group parity with PLP/drawer).
        var card = window.renderVoyadoCard(ps[0], { quickAdd: true, siblings: ps, gallery: true, priority: opts.eagerFirst !== false && i === 0 });
        // Wrap each card in the product-row slide wrapper (L-VPR-2): width classes
        // + slide semantics the scroll engine + a11y need (verbatim product-row-track).
        var slide = document.createElement('div');
        slide.className = 'relative flex flex-col shrink-0 snap-start w-5/6 md:w-50-50-card-tablet lg:w-product-row-card-desktop';
        slide.setAttribute('data-media-card', '');
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', (i + 1) + ' of ' + groups.length);
        slide.appendChild(card);
        frag.appendChild(slide);
      });
      track.replaceChildren(frag);

      // Bind the injected cards (gallery/swatches/wishlist) — L-VPR-3: the shipped
      // voyado-card-behaviour.js listens for this on document.
      document.dispatchEvent(new CustomEvent('voyado:cards-rendered', { detail: { root: track } }));

      section.setAttribute('data-voyado-state', 'results');
      initScroll(groups.length);
    }

    // ---- the carousel scroll engine (OWNED here, NOT product-row.js — L-VPR-1) ----
    // Replicates product-row.js mechanics against the injected slides.
    var currentIndex = 0, itemsVisible = 1, initialOffset = 0, slideCount = 0;

    function initScroll(count) {
      slideCount = count;
      if (rerenders) { currentIndex = 0; track.style.transform = ''; } // re-render reset (L-PR-2)
      track.setAttribute('data-slider-track-init', 'true'); // flips lg:overflow-x-visible (reused class)
      updateLayout();
      calcInitialOffset();
      bindScrollEvents();
      updateButtons();
    }
    function cards() { return track.querySelectorAll('[data-media-card]'); }
    function isDesktop() { return window.innerWidth >= DESKTOP; }
    function calcInitialOffset() {
      var first = track.querySelector('[data-media-card]');
      initialOffset = first ? first.offsetLeft : 0;
    }
    function updateLayout() {
      itemsVisible = isDesktop() ? desktopVisible : 1;   // desktopVisible from the section root (product-row.js:17 contract); standard = 4 (3/12 cols)
      if (!controls) return;
      var hideControls = !isDesktop() || slideCount <= itemsVisible;
      // The markup keeps `hidden` (mobile) at all times; desktop visibility is the
      // `lg:flex` utility, toggled here — so fewer than 5 products (slideCount <=
      // desktopVisible) shows NO arrows on desktop instead of disabled ones.
      controls.classList.toggle('lg:flex', !hideControls);
      // FAILS §2 / shared-invariant 6 — never set aria-hidden to the string value; pair aria-hidden with inert.
      if (hideControls) { controls.setAttribute('aria-hidden', 'true'); }
      else { controls.removeAttribute('aria-hidden'); }
      controls.toggleAttribute('inert', hideControls);
    }
    function goTo(index) {
      var list = cards();
      if (!list.length) return;
      currentIndex = Math.max(0, Math.min(index, slideCount - itemsVisible));
      var target = list[currentIndex];
      if (target) {
        var offset = target.offsetLeft;
        if (isDesktop()) {
          // translate the track — the CSS translate move is animated by the reused
          // lg:transition-transform class (compiles to transition-property:
          // transform,translate,scale,rotate), so NO inline transition is written
          // here and NO transition string is added (FAILS §4).
          track.style.transform = 'translateX(-' + (offset - initialOffset) + 'px)';
        } else {
          track.style.transform = '';
          track.scrollLeft = offset;
        }
      }
      updateButtons();
    }
    function updateButtons() {
      setBtn(prevBtn, currentIndex === 0);
      setBtn(nextBtn, currentIndex >= slideCount - itemsVisible);
    }
    function setBtn(btn, disabled) {
      if (!btn) return;
      btn.toggleAttribute('disabled', disabled);
      btn.setAttribute('aria-disabled', String(disabled));
    }
    var scrollBound = false;
    function bindScrollEvents() {
      if (scrollBound) return; // re-renders reuse the same track; bind listeners once (L-PR-2)
      scrollBound = true;
      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(currentIndex - 1); }, { signal: ac.signal });
      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(currentIndex + 1); }, { signal: ac.signal });
      var raf;
      track.addEventListener('scroll', function () {
        if (window.innerWidth >= DESKTOP) return;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          var cw = (track.querySelector('[data-media-card]') || {}).offsetWidth || track.offsetWidth;
          var snapped = Math.round(track.scrollLeft / cw);
          if (snapped !== currentIndex) { currentIndex = snapped; updateButtons(); }
        });
      }, { passive: true, signal: ac.signal });
      var w = window.innerWidth;
      window.addEventListener('resize', function () {
        if (window.innerWidth === w) return;  // iOS address-bar guard (product-row.js parity)
        w = window.innerWidth;
        updateLayout(); calcInitialOffset(); goTo(currentIndex); updateButtons();
      }, { passive: true, signal: ac.signal });
    }

    function destroy() { ac.abort(); if (io) io.disconnect(); }

    return { render: render, destroy: destroy };
  }

  window.VoyadoCarousel = {
    attach: function (section, opts) {
      if (instances.has(section)) return instances.get(section);   // idempotent (double-attach guard, WeakMap)
      var ctl = createController(section, opts);
      instances.set(section, ctl);
      return ctl;                       // { render(groups), destroy() }
    },
    detach: function (section) {
      var ctl = instances.get(section);
      if (ctl) { ctl.destroy(); instances.delete(section); }
    }
  };
})();
